import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Upload, Loader2, FileText, Check } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { adminListEmailTemplates, adminCreateEmailTemplate, adminDeleteEmailTemplate, adminUpdateEmailTemplate } from '../../services/api';
import AdminDashboardNav from '../../components/dashboard/AdminDashboardNav';

interface EmailTemplate {
  id: number;
  name: string;
  compiledHtml: string | null;
  createdAt: number;
  updatedAt: number;
}

export default function AdminTemplatesPage() {
  const { token } = useAuthStore();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [uploading, setUploading] = useState<number | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<number | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  const fetchTemplates = async () => {
    try {
      const res = await adminListEmailTemplates(token!);
      if (res.ok) {
        const json = await res.json() as { data: EmailTemplate[] };
        setTemplates(json.data);
      }
    } catch {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, [token]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const res = await adminCreateEmailTemplate({ name: newName.trim() }, token!);
      if (res.ok) {
        setNewName('');
        await fetchTemplates();
      } else {
        const err = await res.json() as any;
        setError(err.error || 'Failed to create');
      }
    } catch {
      setError('Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this template?')) return;
    try {
      const res = await adminDeleteEmailTemplate(id, token!);
      if (res.ok) {
        await fetchTemplates();
      } else {
        const err = await res.json() as any;
        setError(err.error || 'Failed to delete');
      }
    } catch {
      setError('Failed to delete template');
    }
  };

  const handleUploadClick = (id: number) => {
    setSelectedTemplateId(id);
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTemplateId) return;

    setUploading(selectedTemplateId);
    setUploadSuccess(null);
    setError('');

    try {
      const content = await file.text();
      const isHtml = file.name.toLowerCase().endsWith('.html') || file.name.toLowerCase().endsWith('.htm');
      if (!isHtml) {
        setError('Only .html or .htm template files are supported');
        return;
      }

      const payload = { compiledHtml: content };

      const res = await adminUpdateEmailTemplate(selectedTemplateId, payload, token!);
      if (res.ok) {
        setUploadSuccess(selectedTemplateId);
        setTimeout(() => setUploadSuccess(null), 3000);
        await fetchTemplates();
      } else {
        setError('Failed to upload template');
      }
    } catch {
      setError('Failed to upload template');
    } finally {
      setUploading(null);
      setSelectedTemplateId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-deep-charcoal text-white">
      <AdminDashboardNav currentPage="templates" />
      <div className="max-w-4xl mx-auto px-6 pt-24 pb-8">
        <div className="flex items-center gap-4 mb-8">
          <h1 className="text-2xl font-bold">Email Templates</h1>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Create new template */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5 mb-6">
          <h2 className="text-sm font-mono uppercase text-electric-cyan tracking-wider mb-4">Add Template</h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Template name..."
              className="flex-1 bg-surface-dark border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="btn-primary !px-5 !py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              Create
            </button>
          </div>
        </motion.div>

        {/* Templates list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="animate-spin text-electric-cyan" />
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((tpl) => (
              <motion.div
                key={tpl.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="glass-card p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-electric-cyan" />
                  <div>
                    <p className="font-medium text-sm">{tpl.name}</p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {tpl.compiledHtml
                        ? 'HTML ready ✓'
                        : 'No template uploaded'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleUploadClick(tpl.id)}
                    disabled={uploading === tpl.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-dark border border-white/10 rounded-lg text-xs hover:border-electric-cyan transition-colors disabled:opacity-50"
                  >
                    {uploading === tpl.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : uploadSuccess === tpl.id ? (
                      <Check size={14} className="text-green-400" />
                    ) : (
                      <Upload size={14} />
                    )}
                    {uploadSuccess === tpl.id ? 'Uploaded!' : 'Upload'}
                  </button>
                  <button
                    onClick={() => handleDelete(tpl.id)}
                    className="p-1.5 text-text-secondary hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
            {templates.length === 0 && (
              <p className="text-center text-text-secondary py-8">No templates yet. Create one above.</p>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".html,.htm"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}
